<template>
  <section-wrapper class="event-header__container">
    <header class="header__container">
      <h1 class="header__title">
        {{ data.attributes.title }}
      </h1>
    </header>

    <template #additional-post>
      <div class="caption__container">
        <ul class="caption-items__list">
          <li
            v-if="data.attributes.event_date"
            :title="useDate(data.attributes.event_date, 'MMM D, YYYY HH:mm')"
            class="list__item"
          >
            <icon-clock class="item__icon" />

            {{ useDate(data.attributes.event_date) }}
          </li>

          <li v-if="data.attributes.event_location" class="list__item">
            <icon-map-pin class="item__icon" />

            {{ data.attributes.event_location }}
          </li>
        </ul>

        <div class="caption__buttons">
          <app-button
            :download="`${data.attributes.title} event`"
            :url="exportCalendarUrl"
            class="event-button"
            small
            target="_blank"
          >
            {{ $t("event.addToCalendar") }}
          </app-button>

          <app-button
            v-if="data.attributes.map_url"
            :url="data.attributes.map_url"
            small
            target="_blank"
            variant="primary"
          >
            {{ $t("event.showMap") }}
          </app-button>
        </div>
      </div>
    </template>
  </section-wrapper>
</template>

<script lang="ts" setup>
import { useEventCalendarDate } from "~/modules/events/composables/useEventCalendarDate";
import { useExportEventUrl } from "~/modules/events/composables/useExportEventUrl";

import type { Event } from "~/modules/events/ts/Event";
import AppButton from "~/modules/shared/components/AppButton.vue";
import IconClock from "~/modules/shared/components/icons/IconClock.vue";
import IconMapPin from "~/modules/shared/components/icons/IconMapPin.vue";
import SectionWrapper from "~/modules/shared/components/SectionWrapper.vue";
import { useDate } from "~/modules/shared/composables/useDate";

const props = defineProps<{
  data: Event;
}>();

const eventDateParsed = computed(() =>
  useEventCalendarDate(props.data.attributes.event_date),
);
const exportCalendarUrl = computed(() =>
  useExportEventUrl({
    text: props.data.attributes.title || "",
    dates: [eventDateParsed.value, eventDateParsed.value],
    details: props.data.attributes.introduction || "",
    location: props.data.attributes.event_location || "",
  }),
);
</script>

<style lang="scss" scoped>
.header__title {
  @apply mb-6 text-title-sm lg:text-title-md text-center;
  color: white;
}

.caption__container {
  @apply p-12;
  border-top: 1px solid theme("colors.primary-100");
  border-bottom: 1px solid theme("colors.primary-100");
}

.caption-items__list {
  @apply flex flex-col sm:flex-row sm:items-center justify-center w-full text-body-xxs text-primary-400;

  .list__item {
    @apply flex items-center sm:mr-10 mb-6 sm:mb-0;

    .item__icon {
      @apply mr-3;
    }

    &:last-child {
      @apply mr-0;
    }
  }
}

.caption__buttons {
  @apply flex flex-col sm:flex-row sm:items-center justify-center w-full mt-10 text-center;
}

.event-button {
  @apply sm:mr-10 mb-10 sm:mb-0;

  &:last-child {
    @apply mr-0;
  }
}
</style>
